/**
 * Follows a treatment from the page that shows it.
 *
 * Attach to an element with:
 *   data-job-monitor
 *   data-job-result-url="/path/to/the/result"
 *   data-job-stalled-text="…"        (optional: what to say when it gives up waiting)
 *
 * The result endpoint speaks the monitor's contract: 202 + Retry-After while the treatment runs,
 * anything else once it has settled — the answer, 422 with the reason, 410 once the return has gone.
 * This component needs only the first half of it. It asks with HEAD, so a document is never fetched
 * before the reader clicks it, at the rhythm the server names; and the moment the treatment settles
 * it hands the page back to the server by reloading it — the page knows how to show a result, an
 * error, a loss, and the JSON reader gets the same fields. No progress bar: a render is one call,
 * and a bar would only invent a number. What is shown is that something is happening, and the name
 * of the treatment, which the page already carries.
 *
 * Without JavaScript the page refreshes itself every two seconds (a <noscript> meta); with it, this
 * lighter poll and a single reload take over. Themes style nothing here: the indicator is an inline
 * SVG in the text's own colour, and it stays still when the reader asked for reduced motion.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';
const DEFAULT_WAIT_MS = 2000;
const MAX_WAIT_MS = 30000;
const GIVE_UP_AFTER_MS = 15 * 60 * 1000;

function reducedMotion() {
    return typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function indicator() {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '1.25em');
    svg.setAttribute('height', '1.25em');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.style.verticalAlign = '-0.3em';
    svg.style.marginRight = '0.5em';

    const arc = document.createElementNS(SVG_NS, 'circle');
    arc.setAttribute('cx', '12');
    arc.setAttribute('cy', '12');
    arc.setAttribute('r', '9');
    arc.setAttribute('fill', 'none');
    arc.setAttribute('stroke', 'currentColor');
    arc.setAttribute('stroke-width', '3');
    arc.setAttribute('stroke-linecap', 'round');
    arc.setAttribute('stroke-dasharray', '40 17');

    if (!reducedMotion()) {
        const spin = document.createElementNS(SVG_NS, 'animateTransform');
        spin.setAttribute('attributeName', 'transform');
        spin.setAttribute('type', 'rotate');
        spin.setAttribute('from', '0 12 12');
        spin.setAttribute('to', '360 12 12');
        spin.setAttribute('dur', '1s');
        spin.setAttribute('repeatCount', 'indefinite');
        arc.appendChild(spin);
    }
    svg.appendChild(arc);
    return svg;
}

function waitNamedBy(response) {
    const seconds = Number(response.headers.get('Retry-After'));
    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : DEFAULT_WAIT_MS;
}

function stall(el, spinner) {
    spinner.remove();
    const said = el.dataset.jobStalledText;
    if (said) {
        el.textContent = said;
    }
    el.classList.add('is-stalled');
}

function follow(el) {
    const url = el.dataset.jobResultUrl;
    const spinner = indicator();
    el.prepend(spinner);
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-busy', 'true');

    const startedAt = Date.now();
    let failures = 0;

    const tick = async () => {
        if (Date.now() - startedAt > GIVE_UP_AFTER_MS) {
            stall(el, spinner);
            return;
        }
        let wait = DEFAULT_WAIT_MS;
        try {
            const response = await fetch(url, {
                method: 'HEAD',
                cache: 'no-store',
                credentials: 'same-origin',
                headers: { Accept: '*/*' }
            });
            if (response.status !== 202) {
                // Settled, failed, gone or refused: the server's page says which.
                window.location.reload();
                return;
            }
            wait = waitNamedBy(response);
            failures = 0;
        } catch (error) {
            // The network, not the treatment: back off, and keep asking.
            failures += 1;
            wait = Math.min(MAX_WAIT_MS, DEFAULT_WAIT_MS * 2 ** failures);
        }
        window.setTimeout(tick, wait);
    };
    window.setTimeout(tick, DEFAULT_WAIT_MS);
}

export function initJobMonitors(root = document) {
    root.querySelectorAll('[data-job-monitor][data-job-result-url]').forEach(el => {
        if (el.dataset.jobMonitorBound === 'true') return;
        el.dataset.jobMonitorBound = 'true';
        follow(el);
    });
}

document.addEventListener('DOMContentLoaded', () => initJobMonitors());
